import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus } from "lucide-react";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const U = (id, w = 900) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

const WORKS = [
  { id: "w-restaurante", src: "/work/restaurante-led.jpeg", cat: "reformas", es: "Reforma de restaurante con iluminación LED", en: "Restaurant renovation with LED lighting" },
  { id: "w-cuadros", src: "/work/cuadros-electricos.jpeg", cat: "electricidad", es: "Cuadros eléctricos nuevos", en: "New consumer units" },
  { id: "w-solera", src: "/work/solera-hormigon.jpeg", cat: "reformas", es: "Solera de hormigón armado", en: "Reinforced concrete slab" },
  { id: "w-bar", src: "/work/reforma-local-bar.webp", cat: "reformas", es: "Reforma integral de local comercial", en: "Full commercial fit-out" },
  { id: "w-puerta", src: "/work/puerta-corredera.webp", cat: "reformas", es: "Puerta corredera a medida", en: "Bespoke sliding door" },
  { id: "w-armario", src: "/work/armario-industrial.jpeg", cat: "electricidad", es: "Armario eléctrico industrial", en: "Industrial switchgear" },
  { id: "w-pladur", src: "/work/techo-pladur.jpeg", cat: "reformas", es: "Techo de Pladur y enlucido", en: "Drywall ceiling & plastering" },
  { id: "w-muros", src: "/work/muros-bloque.webp", cat: "reformas", es: "Levantado de muros de bloque", en: "Block wall construction" },
  { id: "photo-1584622650111-993a426fbf0a", cat: "fontaneria", es: "Trabajo de fontanería", en: "Plumbing works" },
  { id: "photo-1635424710928-0544e8512eae", cat: "climatizacion", es: "Aire acondicionado", en: "Air conditioning" },
];

const Gallery = () => {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible = WORKS.filter((w) => filter === "all" || w.cat === filter);

  return (
    <section
      id="proyectos"
      data-testid="gallery-section"
      className="bg-travertine py-28 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <div className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p
                data-testid="gallery-eyebrow"
                className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
              >
                {t.gallery.eyebrow}
              </p>
              <h2
                data-testid="gallery-heading"
                className="mt-5 font-display text-3xl font-medium tracking-tight text-charcoal sm:text-4xl"
              >
                {t.gallery.heading}
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-500">
                {t.gallery.sub}
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
              {t.gallery.filters.map((f) => (
                <button
                  key={f.id}
                  data-testid={`gallery-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    filter === f.id
                      ? "border-terracotta bg-terracotta text-bone"
                      : "border-black/15 text-stone-600 hover:border-terracotta hover:text-terracotta"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-2 gap-4 lg:grid-cols-5 lg:gap-5" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((w, i) => (
              <motion.figure
                layout
                key={w.id}
                data-testid={`gallery-item-${w.cat}-${i}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="group relative cursor-pointer overflow-hidden bg-stone-200"
                onClick={() => setActive(w)}
              >
                <img
                  src={w.src || U(w.id)}
                  alt={lang === "es" ? w.es : w.en}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-end justify-between bg-charcoal/0 p-4 transition-colors duration-500 group-hover:bg-charcoal/40">
                  <span className="translate-y-3 bg-bone/95 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    {lang === "es" ? w.es : w.en}
                  </span>
                  <span className="flex h-8 w-8 translate-y-3 items-center justify-center bg-terracotta text-bone opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <Plus size={15} />
                  </span>
                </div>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>

        <p data-testid="gallery-note" className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">
          {t.gallery.note}
        </p>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/90 p-5 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.src || U(active.id, 1600)}
                alt={lang === "es" ? active.es : active.en}
                className="max-h-[78vh] w-full object-contain"
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-300">
                  {lang === "es" ? active.es : active.en}
                </p>
                <button
                  data-testid="lightbox-close-btn"
                  onClick={() => setActive(null)}
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-stone-400 transition-colors hover:text-terracotta"
                >
                  {t.gallery.close} <X size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
